/**
 * SignalR Service
 * Manages SignalR connection lifecycle and hub communication
 */

import * as signalR from "@microsoft/signalr";
import { apiClient } from "../client";
import { getApiBaseUrl } from "../../config";

class SignalRService {
  private connection: signalR.HubConnection | null = null;
  private hubUrl: string | null = null;
  private useWebSocketOnly: boolean = false; // Track if we should use WebSocket only (skip negotiation)

  /**
   * Check if WebSocket is supported in the browser
   */
  private isWebSocketSupported(): boolean {
    return typeof WebSocket !== "undefined";
  }

  /**
   * Get the SignalR hub URL from API base URL
   * Converts: https://orderapi.pharmasoft.biz/api -> https://orderapi.pharmasoft.biz/hubs/notifications
   * For WebSocket connections, SignalR will automatically convert https:// to wss://
   */
  private getHubUrl(): string {
    if (this.hubUrl) {
      return this.hubUrl;
    }

    const apiBaseUrl = getApiBaseUrl();
    // Remove trailing slash if present
    const cleanUrl = apiBaseUrl.endsWith("/") ? apiBaseUrl.slice(0, -1) : apiBaseUrl;
    
    // Replace /api with /hubs/notifications if /api exists in the path
    if (cleanUrl.endsWith("/api")) {
      this.hubUrl = cleanUrl.replace("/api", "/hubs/notifications");
    } else {
      // If no /api suffix, append /hubs/notifications
      this.hubUrl = `${cleanUrl}/hubs/notifications`;
    }

    return this.hubUrl;
  }

  /**
   * Create and configure SignalR connection
   */
  private createConnection(): signalR.HubConnection {
    const token = apiClient.getToken();
    const hubUrl = this.getHubUrl();

    if (!token) {
      throw new Error("No authentication token available");
    }

    // Build connection options
    // Strategy: Try WebSocket with skipNegotiation first to bypass CORS
    // If that fails, fall back to normal negotiation
    const connectionOptions: signalR.IHttpConnectionOptions = {
      // Send token via accessTokenFactory
      // For WebSocket connections with skipNegotiation, SignalR will append it as ?access_token=...
      // This matches the backend JWT Bearer configuration that reads from query string
      // For other transports, it will be sent in Authorization header
      // accessTokenFactory is called each time a new connection is established or reconnected
      accessTokenFactory: () => {
        // Always get the latest token (handles token refresh)
        const currentToken = apiClient.getToken();
        if (!currentToken) {
          throw new Error("No authentication token available");
        }
        return currentToken;
      },
      // Use WebSockets with skipNegotiation to bypass CORS preflight
      // This only works if WebSockets are available
      transport: this.useWebSocketOnly 
        ? signalR.HttpTransportType.WebSockets 
        : signalR.HttpTransportType.WebSockets | signalR.HttpTransportType.LongPolling,
      // Skip negotiation for WebSocket connections to avoid CORS preflight
      // When skipNegotiation is true, SignalR connects directly via WebSocket
      // and appends ?access_token=... to the URL, which the backend reads in OnMessageReceived
      skipNegotiation: this.useWebSocketOnly,
    };

    const connection = new signalR.HubConnectionBuilder()
      .withUrl(hubUrl, connectionOptions)
      .withAutomaticReconnect({
        nextRetryDelayInMilliseconds: (retryContext) => {
          // Exponential backoff: 0s, 2s, 10s, 30s, then 30s intervals
          if (retryContext.previousRetryCount === 0) return 0;
          if (retryContext.previousRetryCount === 1) return 2000;
          if (retryContext.previousRetryCount === 2) return 10000;
          return 30000;
        },
      })
      .configureLogging(
        import.meta.env.PROD ? signalR.LogLevel.Error : signalR.LogLevel.Warning
      ) // Only log errors in production, warnings in development
      .build();

    // Handle connection errors
    connection.onclose((error) => {
      if (error) {
        console.error("SignalR connection closed with error:", error);
      }
    });

    return connection;
  }

  /**
   * Start SignalR connection
   */
  async start(): Promise<void> {
    if (this.connection?.state === signalR.HubConnectionState.Connected) {
      return;
    }

    if (this.connection?.state === signalR.HubConnectionState.Connecting) {
      // Wait for connection to complete
      return new Promise((resolve, reject) => {
        const timeout = setTimeout(() => {
          reject(new Error("Connection timeout"));
        }, 10000);

        const checkConnection = () => {
          if (this.connection?.state === signalR.HubConnectionState.Connected) {
            clearTimeout(timeout);
            resolve();
          } else if (this.connection?.state === signalR.HubConnectionState.Disconnected) {
            clearTimeout(timeout);
            reject(new Error("Connection failed"));
          } else {
            setTimeout(checkConnection, 100);
          }
        };
        checkConnection();
      });
    }

    // Check if WebSocket is supported
    if (!this.isWebSocketSupported()) {
      if (!import.meta.env.PROD) {
        console.warn("WebSocket is not supported in this browser. Using negotiation fallback.");
      }
      this.useWebSocketOnly = false;
    }

    try {
      // First, try with WebSocket only (skip negotiation) to avoid CORS
      // WebSocket connections bypass CORS preflight checks
      // Token will be sent as ?access_token=... query parameter (matches backend JWT Bearer config)
      if (this.isWebSocketSupported()) {
        this.useWebSocketOnly = true;
        this.connection = this.createConnection();
        
        if (!import.meta.env.PROD) {
          console.log(`Attempting SignalR WebSocket connection to: ${this.getHubUrl()}`);
          console.log(`Skip negotiation: ${this.useWebSocketOnly}`);
          console.log(`Token will be sent as ?access_token=... query parameter`);
        }
        
        await this.connection.start();
        if (!import.meta.env.PROD) {
          console.log("SignalR connection established via WebSocket (skip negotiation)");
        }
        return; // Success, exit early
      }
    } catch (error: any) {
      if (!import.meta.env.PROD) {
        console.warn("WebSocket connection failed:", error);
      }
      
      // Check if error is specifically about negotiation/CORS
      const isCorsError = error?.message?.includes("CORS") || 
                         error?.message?.includes("Failed to fetch") ||
                         error?.message?.includes("negotiate");
      
      if (isCorsError && this.useWebSocketOnly && !import.meta.env.PROD) {
        // If it's a CORS/negotiation error with skipNegotiation, the WebSocket might not be available
        // Try again without skipNegotiation, but this will likely still fail due to CORS
        console.warn("CORS error detected. WebSocket may not be available. Trying with negotiation...");
      }
      
      // If WebSocket-only fails, try with negotiation
      // NOTE: This may still fail due to CORS if backend is not configured correctly
      // Backend needs: Access-Control-Allow-Origin: <specific-origin> (not *)
      // and Access-Control-Allow-Credentials: true
      try {
        this.useWebSocketOnly = false;
        this.connection = this.createConnection();
        await this.connection.start();
        if (!import.meta.env.PROD) {
          console.log("SignalR connection established with negotiation");
        }
      } catch (fallbackError: any) {
        console.error("SignalR connection error (all methods failed):", fallbackError);
        
        // Provide helpful error message
        if (fallbackError?.message?.includes("CORS") || fallbackError?.message?.includes("Failed to fetch")) {
          console.error("\n=== CORS Configuration Required ===");
          console.error("Backend needs to configure CORS properly in Program.cs:");
          console.error(`
builder.Services.AddCors(options =>
{
    options.AddPolicy("AllowSpecificOrigin", policy =>
    {
        policy.WithOrigins("http://localhost:8090", "https://your-production-domain.com")
              .AllowCredentials()  // Required when using credentials
              .AllowAnyHeader()
              .AllowAnyMethod();
    });
});

// Apply CORS before SignalR
app.UseCors("AllowSpecificOrigin");
app.MapHub<NotificationsHub>("/hubs/notifications");
          `);
        }
        
        // Reset connection
        this.connection = null;
        throw fallbackError;
      }
    }
  }

  /**
   * Stop SignalR connection
   */
  async stop(): Promise<void> {
    if (this.connection) {
      try {
        await this.connection.stop();
        if (!import.meta.env.PROD) {
          console.log("SignalR connection stopped");
        }
      } catch (error) {
        console.error("Error stopping SignalR connection:", error);
      } finally {
        this.connection = null;
      }
    }
  }

  /**
   * Join a firm group
   */
  async joinFirmGroup(firmId: number): Promise<void> {
    if (!this.connection) {
      throw new Error("SignalR connection not established");
    }

    if (this.connection.state !== signalR.HubConnectionState.Connected) {
      await this.start();
    }

    try {
      await this.connection.invoke("JoinFirmGroup", String(firmId));
      if (!import.meta.env.PROD) {
        console.log(`Joined firm group: firm-${firmId}`);
      }
    } catch (error) {
      console.error(`Error joining firm group firm-${firmId}:`, error);
      throw error;
    }
  }

  /**
   * Leave a firm group
   */
  async leaveFirmGroup(firmId: number): Promise<void> {
    if (!this.connection || this.connection.state !== signalR.HubConnectionState.Connected) {
      return;
    }

    try {
      await this.connection.invoke("LeaveFirmGroup", String(firmId));
      if (!import.meta.env.PROD) {
        console.log(`Left firm group: firm-${firmId}`);
      }
    } catch (error) {
      console.error(`Error leaving firm group firm-${firmId}:`, error);
    }
  }

  /**
   * Register event handler for OrderCreated event
   */
  onOrderCreated(callback: (data: any) => void): void {
    if (!this.connection) {
      throw new Error("SignalR connection not established");
    }

    this.connection.on("OrderCreated", callback);
  }

  /**
   * Register event handler for ReceiptCreated event
   */
  onReceiptCreated(callback: (data: any) => void): void {
    if (!this.connection) {
      throw new Error("SignalR connection not established");
    }

    this.connection.on("ReceiptCreated", callback);
  }

  /**
   * Remove event handler for OrderCreated event
   */
  offOrderCreated(callback?: (data: any) => void): void {
    if (!this.connection) {
      return;
    }

    if (callback) {
      this.connection.off("OrderCreated", callback);
    } else {
      this.connection.off("OrderCreated");
    }
  }

  /**
   * Remove event handler for ReceiptCreated event
   */
  offReceiptCreated(callback?: (data: any) => void): void {
    if (!this.connection) {
      return;
    }

    if (callback) {
      this.connection.off("ReceiptCreated", callback);
    } else {
      this.connection.off("ReceiptCreated");
    }
  }

  /**
   * Get current connection state
   */
  getConnectionState(): signalR.HubConnectionState {
    return this.connection?.state ?? signalR.HubConnectionState.Disconnected;
  }

  /**
   * Check if connection is established
   */
  isConnected(): boolean {
    return this.connection?.state === signalR.HubConnectionState.Connected;
  }

  /**
   * Register connection state change handler
   */
  onStateChange(callback: (state: signalR.HubConnectionState) => void): void {
    if (!this.connection) {
      return;
    }

    this.connection.onclose(() => {
      callback(signalR.HubConnectionState.Disconnected);
    });

    this.connection.onreconnecting(() => {
      callback(signalR.HubConnectionState.Reconnecting);
    });

    this.connection.onreconnected(() => {
      callback(signalR.HubConnectionState.Connected);
    });
  }
}

// Export singleton instance
export const signalRService = new SignalRService();

